import { createContext, useState, useCallback, useEffect } from 'react';
import { LocalStorageService } from '../services';
import { z } from 'zod';
import * as Sentry from '@sentry/react';
import { DocumentType, documentTypes } from '../types';

type AuthContextType = {
  login: (user: string, documentType?: DocumentType, documentNumber?: string, orgId?: string) => void;
  logout: () => void;
  setDocumentNumber: (documentNumber: string | undefined) => void;
  user?: string;
  documentType?: DocumentType;
  documentNumber?: string;
  orgId?: string;
};
// orgId is only set when the user logged in through the org login.
type AuthStatus = { user: string; documentType?: DocumentType; orgId?: string } | undefined;

const AuthContext = createContext<AuthContextType>({
  login: () => {},
  logout: () => {},
  setDocumentNumber: () => {},
});

const authToken = LocalStorageService.getAuthorizationToken();
const parsed = z
  .object({ user: z.string(), documentType: z.enum(documentTypes).optional(), orgId: z.string().optional() })
  .safeParse(authToken && JSON.parse(authToken));

const AuthContextProvider = ({ children }: React.PropsWithChildren): JSX.Element => {
  const [authStatus, setAuthStatus] = useState<AuthStatus>(parsed.success ? parsed.data : undefined);
  const [documentNumber, setDocumentNumber] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (authStatus) Sentry.setUser({ id: authStatus.user });
  }, [authStatus]);

  const login = useCallback((user: string, documentType?: DocumentType, documentNumber?: string, orgId?: string) => {
    const userTrimmed = user.trim();
    const token = JSON.stringify({ user: userTrimmed, documentType, orgId });
    LocalStorageService.setAuthorizationToken(token);
    setAuthStatus({ user: userTrimmed, documentType, orgId });
    setDocumentNumber(documentNumber);
  }, []);

  const logout = useCallback(() => {
    LocalStorageService.removeAuthorizationToken();
    setAuthStatus(undefined);
    setDocumentNumber(undefined);
  }, []);

  return (
    <AuthContext.Provider value={{ ...authStatus, documentNumber, setDocumentNumber, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
export { AuthContextProvider };
